import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
} from "@mui/material";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { requestPasswordReset } from "../api/authApi";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPassword,
});

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess(true);
    } catch (err: unknown) {
      const error = err as { status?: number; body?: { detail?: string } };
      if (error.status === 429) {
        setError("Too many requests. Please try again later.");
      } else {
        setError(
          error.body?.detail ||
          "Failed to send reset email. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              padding: 4,
              width: "100%",
              background: "linear-gradient(145deg, #0f1420 0%, #141824 100%)",
              border: "1px solid rgba(59, 130, 246, 0.1)",
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                fontWeight: 600,
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Check Your Email
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We've sent a password reset link to {email}. Please check your
              inbox and follow the instructions to reset your password.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate({ to: "/login" })}
              sx={{
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                },
              }}
            >
              Back to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            padding: 4,
            width: "100%",
            background: "linear-gradient(145deg, #0f1420 0%, #141824 100%)",
            border: "1px solid rgba(59, 130, 246, 0.1)",
            borderRadius: 2,
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            sx={{
              mb: 2,
              textAlign: "center",
              fontWeight: 600,
              background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Reset Password
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            Enter your email address and we'll send you a link to reset your
            password.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mb: 2,
                py: 1.5,
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                },
              }}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              component={Link}
              to="/login"
              sx={{
                borderColor: "#6b7280",
                color: "#9ca3af",
                "&:hover": {
                  borderColor: "#9ca3af",
                },
              }}
            >
              Back to Login
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}